import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  Shield,
  Home as HomeIcon,
  TrendingUp,
  Users,
  Briefcase,
  Stethoscope,
  Cross,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import InsuranceInquiryModal from "../components/InsuranceInquiryModal";
import { DIVISIONS } from "../lib/divisions";
import { INSURANCE_PRODUCTS_LIST } from "../lib/insuranceProducts";

const productIcons = {
  "life-insurance": Heart,
  "final-expense": Shield,
  "mortgage-protection": HomeIcon,
  "income-protection": Briefcase,
  "family-protection": Users,
  "retirement": TrendingUp,
  "medicare": Cross,
  "health-insurance": Stethoscope,
};

const InsurancePage = () => {
  const div = DIVISIONS.insurance;
  const [showInquiry, setShowInquiry] = useState(false);

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#C89B3C]/10 rounded-full blur-3xl" />
        <div className="max-w-6xl mx-auto px-6 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-3 block">
              Insurance Solutions
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B2343] leading-tight mb-6 max-w-3xl">
              Protect what matters most.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-8">
              Life, final expense, mortgage, income, family, retirement, Medicare and health
              coverage — for veterans, military families, civilians, and small businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/insurance/consultation"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="insurance-hero-consultation-cta"
                className="bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
              >
                Start My Consultation
                <ArrowRight size={18} />
              </a>
              <button
                onClick={() => setShowInquiry(true)}
                data-testid="insurance-inquiry-cta"
                className="bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-all inline-flex items-center justify-center gap-2"
              >
                Quick Question
              </button>
              <a
                href={div.phoneHref}
                data-testid="insurance-phone-cta"
                className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone size={18} />
                {div.phone}
              </a>
            </div>
            <p className="text-sm text-slate-500 mt-4 max-w-2xl">
              Ready to take the next step? Complete our secure consultation form so we can
              better understand your goals and prepare for a meaningful conversation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <SectionHeading
            eyebrow="Our Solutions"
            title="Coverage built around your life."
            subtitle="Every recommendation starts with an insurance needs assessment — never a sales pitch."
            align="center"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INSURANCE_PRODUCTS_LIST.map((p, idx) => {
              const Icon = productIcons[p.slug] || Heart;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  className="group bg-white p-6 border border-slate-200 hover:border-[#C89B3C] hover:shadow-lg transition-all flex flex-col"
                  data-testid={`insurance-product-${p.slug}`}
                >
                  <div className="w-12 h-12 bg-[#0B2343] flex items-center justify-center mb-4 group-hover:bg-[#C89B3C] transition-colors">
                    <Icon className="text-[#C89B3C] group-hover:text-white transition-colors" size={22} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">
                    {p.tagline}
                  </p>
                  <Link
                    to={`/insurance/${p.slug}`}
                    className="inline-flex items-center gap-2 text-[#0B2343] font-medium text-sm hover:text-[#C89B3C] transition-colors border-t border-slate-200 pt-4"
                    data-testid={`insurance-product-link-${p.slug}`}
                  >
                    Learn more <ArrowRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Needs Assessment CTA */}
      <section className="py-20 bg-[#0B2343] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <div className="h-[3px] w-14 bg-[#C89B3C] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Not sure what you need?
          </h2>
          <p className="text-slate-300 mb-8">
            Start with an insurance needs assessment. We'll walk through your situation and
            recommend a plan that fits — no pressure, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/insurance/consultation"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="insurance-bottom-consultation-cta"
              className="bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
            >
              Start My Consultation
              <ArrowRight size={18} />
            </a>
            <button
              onClick={() => setShowInquiry(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B2343] px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
              data-testid="insurance-assessment-cta"
            >
              Quick Needs Assessment
              <ArrowRight size={18} />
            </button>
          </div>
          <p className="text-sm text-slate-400 mt-4 max-w-xl mx-auto">
            Ready to take the next step? Complete our secure consultation form so we can
            better understand your goals and prepare for a meaningful conversation.
          </p>
          <p className="text-slate-400 text-sm mt-6">
            Direct: <a href={div.phoneHref} className="text-[#C89B3C] hover:underline">{div.phone}</a>
          </p>
        </div>
      </section>

      <InsuranceInquiryModal isOpen={showInquiry} onClose={() => setShowInquiry(false)} />
    </div>
  );
};

export default InsurancePage;
